/**
 * Util for creating custom console functions
 * Examples of usage:
 * const logger = createLogger('cl-test')
 * logger.warn('Warning message') --> logs "[cl-test] Warning message"
 * logger.warn.attr('attribute message') --> logs "[cl-test][attr] attribute message"
 * logger.error('Error message', new Error('Something went wrong')) --> logs "[cl-test] Error message", plus an error stack trace
 * logger.group('Group message').log('Indented message') --> logs "[cl-test] Group message", with "Indented message" indented underneath
 */
function createLogger(componentName) {
  const base = `${componentName}:`;
  const createConsoleProxy = (func, message) => {
    return new Proxy(func.bind(console, message), {
      get: (target, prop) => {
        if (prop in target) {
          return target[prop];
        }
        else {
          return createConsoleProxy(target, `[${prop.charAt(0).toUpperCase() + prop.slice(1)}]`);
        }
      },
    });
  };
  const consoleFunctions = {
    assert: createConsoleProxy(console.assert, base),
    clear: createConsoleProxy(console.clear, base),
    count: createConsoleProxy(console.count, base),
    countReset: createConsoleProxy(console.countReset, base),
    debug: createConsoleProxy(console.debug, base),
    dir: createConsoleProxy(console.dir, base),
    dirxml: createConsoleProxy(console.dirxml, base),
    error: createConsoleProxy(console.error, base),
    group: createConsoleProxy(console.group, base),
    groupCollapsed: createConsoleProxy(console.groupCollapsed, base),
    groupEnd: createConsoleProxy(console.groupEnd, base),
    info: createConsoleProxy(console.info, base),
    log: createConsoleProxy(console.log, base),
    table: createConsoleProxy(console.table, base),
    time: createConsoleProxy(console.time, base),
    timeEnd: createConsoleProxy(console.timeEnd, base),
    timeLog: createConsoleProxy(console.timeLog, base),
    trace: createConsoleProxy(console.trace, base),
    warn: createConsoleProxy(console.warn, base),
  };
  return consoleFunctions;
}

/**
 * Determines whether the given string is a valid color
 *
 * @param {string} color
 * @returns {boolean}
 */
function isColor(color) {
  return CSS.supports('fill', color);
}
/**
 * Transforms HSV color array to RGB color array
 *
 * @param hsv [number, number, number]
 * @returns [number, number, number]
 */
function hsvToRgb(hsv) {
  const [h = 0, s = 0, v = 0] = [(hsv === null || hsv === void 0 ? void 0 : hsv[0]) / 360, hsv === null || hsv === void 0 ? void 0 : hsv[1], hsv === null || hsv === void 0 ? void 0 : hsv[2]];
  let [r, g, b] = [0, 0, 0];
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      [r, g, b] = [v, t, p];
      break;
    case 1:
      [r, g, b] = [q, v, p];
      break;
    case 2:
      [r, g, b] = [p, v, t];
      break;
    case 3:
      [r, g, b] = [p, q, v];
      break;
    case 4:
      [r, g, b] = [t, p, v];
      break;
    case 5:
      [r, g, b] = [v, p, q];
      break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
/**
 * Transforms RGB color array to HSV color array
 *
 * @param rgb [number, number, number]
 * @returns [number, number, number]
 */
function rgbToHsv(rgb) {
  const [r, g, b] = rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  const v = max / 255;
  const hLookup = {
    [r]: (g - b + d * (g < b ? 6 : 0)) / (6 * d),
    [g]: (b - r + d * 2) / (6 * d),
    [b]: (r - g + d * 4) / (6 * d),
  };
  const h = hLookup[max] || 0;
  return [h * 360, s, v];
}
/**
 * Transforms HEX string to RGB color array
 *
 * @param hex string
 * @returns [number, number, number]
 */
function hexToRgb(hex) {
  const RXHexSplit = /^#? *(([a-f\d]{1,2})([a-f\d]{1,2})([a-f\d]{1,2}))$/i.exec(hex);
  if (RXHexSplit == null) {
    throw new Error(`Invalid hex color string: ${hex}`);
  }
  const rgb = [
    parseInt(RXHexSplit[2], 16),
    parseInt(RXHexSplit[3], 16),
    parseInt(RXHexSplit[4], 16),
  ];
  return rgb;
}
/**
 * Transforms HEX string to HSV color array
 *
 * @param hex string
 * @returns [number, number, number]
 */
function hexToHsv(hex) {
  const rgb = hexToRgb(hex);
  return rgbToHsv(rgb);
}
/**
 * Transforms RGB color array to HEX color string
 *
 * @param rgb [number, number, number]
 * @returns string
 */
function rgbToHex(rgb) {
  return `#${rgb
    .map(hexPart => {
    const hex = hexPart.toString(16).toLocaleUpperCase();
    return hex.length === 1 ? `0${hex}` : hex;
  })
    .join('')}`;
}
/**
 * Transforms HSV color array to HEX string
 *
 * @param hsv [number, number, number]
 * @returns string
 */
function hsvToHex(hsv) {
  const rgb = hsvToRgb(hsv);
  return rgbToHex(rgb);
}
/**
 * Handles validating the given HEX string
 *
 * @param hex string
 * @returns boolean
 */
function isValidHex(hex) {
  return /^#{0,1}([\da-f]{6})$/i.test(hex);
}
/**
 * Handles validating the given RGB string OR array
 *
 * @param rgb string | [number, number, number]
 * @returns boolean
 */
function isValidRgb(rgb) {
  switch (true) {
    case typeof rgb === 'string':
      return /^rgb\(((1?[1-9]?\d|10\d|2[0-4]\d|25[0-5]),\s?){2}((1?[1-9]?\d|10\d|2[0-4]\d|25[0-5])){1}\)$/i.test(rgb);
    case Array.isArray(rgb) && rgb.length === 3:
      return !rgb.map(value => value >= 0 && value <= 255).includes(false);
    default:
      return false;
  }
}

/**
 * Handles updating the CSS styles object with the given value
 * @param {[key: string]: string} styles Styles object
 * @param {string} varName Name of the CSS variable
 * @param {string|number} value Value that should be set for the given CSS variable name
 * @returns {[key: string]: string}
 */
function updateStyles(styles, varName, value) {
  const stylesCopy = Object.assign({}, styles);
  if (!value) {
    delete stylesCopy[varName];
  }
  else {
    stylesCopy[varName] = `${value}`;
  }
  return stylesCopy;
}
/**
 * Handles debouncing the given function for an x amount of milliseconds
 * @param {Function} func Function that will be debounced
 * @param {number} wait Number of milliseconds to wait before executing the function
 * @param {boolean} immediate Boolean indicating if the function should also be invoked once at the beginning
 * @returns {Function}
 */
function debounce(func, wait = 200, immediate = false) {
  let timeout = undefined;
  return function debounced(...args) {
    if (immediate && !timeout) {
      func.apply(this, args);
    }
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      timeout = undefined;
      func.apply(this, args);
    }, wait);
  };
}
/**
 * Function that handles throttling the given callback every x milliseconds
 *
 * @param callback Function that will be debounced
 * @param interval Number of milliseconds to wait before executing the function
 * @param immediate Boolean indicating if the function should also be invoked once at the beginning
 * @returns {Function}
 */
function throttle(callback, interval, immediate = false) {
  let lastTime = 0;
  return function (...args) {
    if (immediate && !lastTime) {
      callback.apply(this, args);
    }
    const now = Date.now();
    if (now - lastTime >= interval) {
      callback.apply(this, args);
      lastTime = now;
    }
  };
}
/**
 * Function that handles clamping the given number between the given min and max values
 *
 * @param {number} num
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}
/**
 * @param element element to search for shadow DOM
 */
const hasShadowDom = (element) => !!element.shadowRoot && !!element.attachShadow;
/**
 *
 * @param name name of hidden input field
 * @param value value of hidden input field
 * @param container container to add input field to
 * @param ignoreHasShadowDom should method ignore existance of shadow DOM, if ignored input field is always added
 * @param isDisabled disabled state of input field
 */
const renderHiddenInputField = (name, id, value, container, ignoreHasShadowDom, isDisabled) => {
  if (hasShadowDom(container) || ignoreHasShadowDom) {
    let inputField = container.querySelector('input.hidden-input');
    if (!inputField) {
      inputField = container.ownerDocument.createElement('input');
      inputField.type = 'hidden';
      inputField.classList.add('hidden-input');
      container.appendChild(inputField);
    }
    inputField.disabled = isDisabled;
    inputField.name = name;
    inputField.id = id;
    inputField.value = value || '';
  }
};
/**
 * Function that handles checking whether the given attribute value is falsy or not
 *
 * @param value Value of the attribute
 * @returns {boolean}
 */
function isUnset(value) {
  return typeof value === 'undefined' || value === null;
}
/**
 * Returns a CSS value to calculate the REM equivalent of the given number
 * @param {number|string} value Number or string to calculate the REM value for
 * @returns {string}
 */
function numToRem(value) {
  return `calc(${value} * var(--size-1, 0.5rem))`;
}
/**
 * Function that returns a function that validates whether the given type is inside the given const asserted type
 */
function createTypeValidator(e) {
  return (value) => Object.values(e).includes(value);
}
/**
 * Handles focusing on the given element by making it editable and then triggering the
 * keyboard focus
 *
 * @param element Element that should be focused
 */
function focusElement(element) {
  if (element) {
    element.setAttribute('contentEditable', 'true');
    element.focus();
    element.removeAttribute('contentEditable');
  }
}
/**
 * Function that handles setting or removing the given value from the given element
 *
 * @param element Element to update the style of
 * @param value String value to set as the value for the given cssAttribute
 * @param cssAttribute CSS attribute to set the value for
 */
function updateElementStyle(element, value, cssAttribute) {
  if (typeof value === 'string') {
    element.style.setProperty(cssAttribute, value);
  }
  else {
    element.style.removeProperty(cssAttribute);
  }
}
/**
 * Function that validates whether the given value is a valid color token and based on that updates the style of the given element
 */
function updateColorElementStyle(element, value, cssAttribute, consoleFn) {
  switch (true) {
    case isUnset(value):
      updateElementStyle(element, undefined, cssAttribute);
      break;
    case isColor(value):
      {
        if (/^var\(/g.test(value)) {
          consoleFn(`Value "${value}" must be wrapped in RGB tags. E.g. "rgb(var(--color-neutral-main))".`);
        }
        else {
          updateElementStyle(element, value, cssAttribute);
        }
      }
      break;
    default:
      consoleFn(`Given invalid value "${value}".`, 'Please make sure to pass a valid color or CSS variable.');
      break;
  }
}
/**
 * Function that handles returning the initials of the given name based on the following rules:
 * - If two names are provided, for example first and last name, use the first capitalised letter of each word;
 * - When one name is provided, the first two letters will be shown. Both are capitalised;
 * - Don't use more than two initials. If a user has multiple names in their first or last name, only use the initial of the first name in combination with the last name;
 *
 * Read more at: https://designsystem.enreach.com/components/avatar/overview#Group%20avatar
 *
 * @param name Name to get the initials of
 */
function getInitials(name) {
  if (!name) {
    return '';
  }
  const splitName = name.split(' ');
  // If only ONE name is given
  if (splitName.length === 1) {
    // And the name contains only ONE character
    if (splitName[0].length === 1) {
      // Return this ONE character
      return splitName[0];
    }
    else {
      // Else return the first TWO characters of the name
      return splitName[0].slice(0, 2);
    }
  }
  else {
    // If MORE THAN ONE names are given, return the first letter of the FIRST name and the first letter of the LAST name
    return `${splitName[0].slice(0, 1)}${splitName[splitName.length - 1].slice(0, 1)}`;
  }
}
/**
 * Handles fetching and returning the value of a CSS parameter
 *
 * @param {string} parameter Name of the CSS parameter
 * @param {HTMLElement} element HTMLElement to look for the CSS parameter
 * @returns {string}
 */
function getCSSValue(parameter, element) {
  var _a;
  return (_a = getComputedStyle(element || document.documentElement)) === null || _a === void 0 ? void 0 : _a.getPropertyValue(parameter);
}
/**
 * Handles the compare function used to determine the sort order
 * It takes into account language and character set variations by using Intl.Collator method
 *
 * @param columnIndex number value that determines the index of the elements being sorted
 * @param ascending boolean value that determines whether the sorting is in ascending order
 * @returns { (a: HTMLElement, b: HTMLElement) => number }
 */
function comparer(columnIndex, ascending) {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return function (a, b) {
    var _a, _b, _c, _d;
    const aText = (_b = (((_a = a.children[columnIndex]) === null || _a === void 0 ? void 0 : _a.innerText) || '').toString()) === null || _b === void 0 ? void 0 : _b.replace(/\n/g, ' ');
    const bText = (_d = (((_c = b.children[columnIndex]) === null || _c === void 0 ? void 0 : _c.innerText) || '').toString()) === null || _d === void 0 ? void 0 : _d.replace(/\n/g, ' ');
    return aText !== '' && bText !== '' ? collator.compare(ascending ? aText : bText, ascending ? bText : aText) : 0;
  };
}
/**
 *
 * @param source element that we get the attributes from
 * @param target element that we copy the attributes to
 */
function copyElementAttributes(source, target) {
  Array.from(source.attributes).forEach(attribute => {
    target.setAttribute(attribute.nodeName === 'id' ? 'data-id' : attribute.nodeName, attribute.nodeValue);
  });
}
/**
 * Determines whether the given value is a number or not
 *
 * @param value Value to check
 * @returns {boolean}
 */
function isNumber(value) {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : false;
  return typeof parsed === 'number' && Number.isFinite(parsed);
}
/**
 * Handles calling preventDefault() on the given event
 *
 * @param {Event} event
 * @returns {void}
 */
function preventDefault(event) {
  event.preventDefault();
}
/**
 * Emits event for component. If deprecated event still exists it emits both events, the new and the deprecated
 */
function emitEvent(instance, event, newDetail, deprecatedDetail) {
  if (!newDetail) {
    throw new Error('No detail given');
  }
  const deprecatedName = event.slice(2);
  const deprecatedEvent = event.startsWith('cl')
    ? deprecatedName.charAt(0).toLowerCase() + deprecatedName.slice(1)
    : event;
  const eventsEmitted = [];
  if (typeof instance[deprecatedEvent] !== 'undefined') {
    if (deprecatedDetail !== undefined) {
      eventsEmitted.push(instance[deprecatedEvent].emit(deprecatedDetail));
    }
    else {
      throw new Error(`${instance.element.localName}: No arguments given for deprecated event "${deprecatedEvent}"`);
    }
  }
  if (newDetail !== undefined) {
    eventsEmitted.push(instance[event].emit(newDetail));
  }
  else {
    throw new Error(`${instance.element.localName}: Invalid arguments given for event "${event}"`);
  }
  return eventsEmitted;
}
const printed = {};
function logOnce(logger, message) {
  if (printed[message])
    return;
  printed[message] = true;
  logger(message);
}
function logDeprecatedEventOnce(logger, oldEvent, newEvent) {
  logOnce(logger.warn.deprecated, `Event '${oldEvent}' has been deprecated. Please use '${newEvent}' instead.`);
}
function isTriggerEvent(event) {
  switch (true) {
    case event.type === 'click':
      return true;
    case event.type === 'keydown':
    case event.type === 'keypress':
      return event.key === 'Enter' || event.code === 'Enter';
    default:
      return false;
  }
}

export { updateColorElementStyle as A, debounce as B, focusElement as C, createLogger as a, clamp as b, createTypeValidator as c, isNumber as d, emitEvent as e, isUnset as f, getCSSValue as g, getInitials as h, isTriggerEvent as i, hsvToHex as j, hsvToRgb as k, logDeprecatedEventOnce as l, isValidHex as m, numToRem as n, hexToRgb as o, hexToHsv as p, isValidRgb as q, rgbToHex as r, rgbToHsv as s, throttle as t, updateElementStyle as u, comparer as v, copyElementAttributes as w, preventDefault as x, updateStyles as y, renderHiddenInputField as z };

//# sourceMappingURL=utils.js.map